/**
 * EGDC V4 PDP — AJAX variant swap + sticky add-to-cart bar.
 *
 * Two custom elements:
 *
 *  <v4-product-info>  — wraps the gallery + buy-box. Intercepts color/size
 *                       picker clicks, fetches the section via Shopify's
 *                       Section Rendering API, swaps the DOM in place, and
 *                       updates history without a full reload. Falls back to
 *                       the picker's native href on any error.
 *
 *  <v4-sticky-atc>    — fixed bar at the bottom of the viewport that fades in
 *                       once the buy-box has scrolled out of view. Stays in
 *                       sync with the active variant by re-reading the
 *                       buy-box DOM after every variant swap.
 */

class V4ProductInfo extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const picker = event.target.closest('[data-variant-id]');
    if (!picker || picker.classList.contains('is-disabled')) return;
    const variantId = picker.dataset.variantId;
    if (!variantId) return;
    // Allow modifier-clicks to open in new tab as the user expects.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    this.swapVariant(variantId, picker.href);
  };

  async swapVariant(variantId, fallbackUrl) {
    this.classList.add('is-loading');
    try {
      const sectionUrl = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;
      const response = await fetch(sectionUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('v4-product-info');
      if (!next) throw new Error('v4-product-info missing in section response');
      // Move all children from the new element into the existing one so
      // sibling state (e.g. the accordion below) and the element identity
      // are preserved.
      this.replaceChildren(...next.childNodes);
      history.replaceState({}, '', `${this.productUrl}?variant=${variantId}`);
      document.dispatchEvent(new CustomEvent('v4:variant-changed', { detail: { variantId } }));
    } catch (error) {
      console.warn('V4 variant swap failed; falling back to navigation.', error);
      if (fallbackUrl) window.location.href = fallbackUrl;
    } finally {
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('v4-product-info')) {
  customElements.define('v4-product-info', V4ProductInfo);
}

class V4StickyAtc extends HTMLElement {
  connectedCallback() {
    this.buyBox = document.querySelector('.v4-buy-box');
    if (!this.buyBox) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Show the bar only after we've scrolled past the buy-box (so it
          // never appears above the buy-box on initial page load).
          const rect = entry.target.getBoundingClientRect();
          if (!entry.isIntersecting && rect.bottom < 0) {
            this.classList.add('is-visible');
          } else {
            this.classList.remove('is-visible');
          }
        }
      },
      { threshold: 0 }
    );
    this.observer.observe(this.buyBox);

    document.addEventListener('v4:variant-changed', this.onVariantChanged);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('v4:variant-changed', this.onVariantChanged);
  }

  onVariantChanged = () => {
    // Re-bind the observer to the freshly-swapped buy-box.
    this.observer.disconnect();
    this.buyBox = document.querySelector('.v4-buy-box');
    if (!this.buyBox) return;
    this.observer.observe(this.buyBox);
    this.syncFromBuyBox();
  };

  syncFromBuyBox() {
    if (!this.buyBox) return;
    const newAtc = this.buyBox.querySelector('.v4-atc');
    const newPriceText = this.buyBox.querySelector('.v4-buy-box__price');
    const formInput = this.buyBox.querySelector('input[name="id"]');

    const stickyLabel = this.querySelector('.v4-sticky-atc__label');
    const stickyPrice = this.querySelector('.v4-sticky-atc__price');
    const stickyFormInput = this.querySelector('input[name="id"]');

    if (stickyLabel && newAtc) stickyLabel.textContent = newAtc.textContent.trim();
    if (stickyPrice && newPriceText) stickyPrice.textContent = newPriceText.textContent.trim();
    if (stickyFormInput && formInput) stickyFormInput.value = formInput.value;
    if (newAtc && newAtc.disabled) {
      this.classList.add('is-soldout');
    } else {
      this.classList.remove('is-soldout');
    }
  }
}

if (!customElements.get('v4-sticky-atc')) {
  customElements.define('v4-sticky-atc', V4StickyAtc);
}

/**
 * <v4-recommendations>  — async-loaded product rail (Completa tu look /
 * También compraron). Reads data-product-id + data-intent + data-limit,
 * fetches Shopify's Product Recommendations endpoint with the
 * v4-product-recommendations section, replaces itself with the rail.
 *
 * If there are no recommendations, the closest [data-rec-section]
 * ancestor is hidden so we don't leave an orphan header on the page.
 */
class V4Recommendations extends HTMLElement {
  async connectedCallback() {
    const productId = this.dataset.productId;
    const intent = this.dataset.intent || 'related';
    const limit = this.dataset.limit || '4';
    if (!productId) return;
    const url = `/recommendations/products?product_id=${productId}&limit=${limit}&intent=${intent}&section_id=v4-product-recommendations`;
    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error(`Recs fetch failed: ${res.status}`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const rail = doc.querySelector('.v4-rec__rail');
      if (rail && rail.children.length > 0) {
        this.replaceWith(rail);
      } else {
        this.hideSection();
      }
    } catch (error) {
      console.warn('V4 recommendations fetch failed:', error);
      this.hideSection();
    }
  }

  hideSection() {
    const parent = this.closest('[data-rec-section]');
    if (parent) parent.style.display = 'none';
  }
}

if (!customElements.get('v4-recommendations')) {
  customElements.define('v4-recommendations', V4Recommendations);
}

/**
 * AJAX add-to-cart for V4 PDP forms.
 *
 * Document-level submit delegation so both the main buy-box form
 * (.v4-pdp-form) and the sticky bottom bar form (.v4-sticky-atc__form)
 * are handled with one listener — and so AJAX swaps that replace the
 * buy-box DOM don't break the binding.
 *
 * Flow:
 *   1. preventDefault on the submit
 *   2. POST FormData to /cart/add.js
 *   3. On 200 → dispatch global `v4:cart-added` event with the line item
 *   4. <v4-cart-drawer> listens, refreshes its own section + opens itself
 *   5. On 422 (out of stock / oversell) → flash button label, restore
 *   6. On any other error → fall back to native form submission
 */
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!form.matches('.v4-pdp-form, .v4-sticky-atc__form')) return;
  event.preventDefault();

  const submitBtn = form.querySelector('button[type="submit"][name="add"]');
  let originalLabel = '';
  if (submitBtn) {
    originalLabel = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.classList.add('is-loading');
    submitBtn.innerHTML = 'Agregando…';
  }

  try {
    const formData = new FormData(form);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/javascript' },
    });

    if (!response.ok) {
      // Shopify returns 422 with a description on validation errors.
      let message = 'No se pudo agregar';
      try {
        const errBody = await response.json();
        message = errBody.description || errBody.message || message;
      } catch (_) {
        // ignore
      }
      throw new Error(message);
    }

    const data = await response.json();
    document.dispatchEvent(new CustomEvent('v4:cart-added', { detail: data }));

    if (submitBtn) {
      submitBtn.classList.remove('is-loading');
      submitBtn.classList.add('is-added');
      submitBtn.innerHTML = '¡Agregado!';
      setTimeout(() => {
        submitBtn.innerHTML = originalLabel;
        submitBtn.classList.remove('is-added');
        submitBtn.disabled = false;
      }, 1500);
    }
  } catch (error) {
    console.warn('V4 ATC failed:', error);
    if (submitBtn) {
      submitBtn.classList.remove('is-loading');
      submitBtn.classList.add('is-error');
      submitBtn.innerHTML = error.message || 'Error';
      setTimeout(() => {
        submitBtn.innerHTML = originalLabel;
        submitBtn.classList.remove('is-error');
        submitBtn.disabled = false;
      }, 2500);
    }
  }
});
